import parc_logo from '@assets/parc.png';
import { useQuestProgress } from '@src/hooks/useQuests';
import { useViewTransition } from '@src/hooks/useViewTransition';
import { Quest } from 'jigsaw';
import { VStack, HStack, Text, buttonVariants, Button, linkStyles } from 'ui_2';
import { Route as QuestRoute } from '@routes/quest/$questId';
import { Link, useNavigate } from '@tanstack/react-router';
import { useDappState } from '@src/hooks/useDappState';

export const QuestBento = ({ quest }: { quest: Quest }) => {
  const { data, isSuccess } = useQuestProgress(quest._id);
  const { slideLeftFullPage } = useViewTransition();
  const { isPuzzleWalletWebview } = useDappState();

  // Calculate progress percentage (assuming current progress is 0 for now)
  const progressPercentage = isSuccess
    ? (data.dailyEarnings / data.dailyLimit) * 100
    : undefined;

  const questComplete = data
    ? data.dailyEarnings === data.dailyLimit && data.dailyEarnings > 0
    : undefined;

  const navigate = useNavigate();

  return (
    <div
      className={`relative w-full max-w-[450px] overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-lg ${
        questComplete && data?.isPro ? 'opacity-70' : ''
      }`}
    >
      <HStack className='items-stretch'>
        {/* Left side - Image */}
        <div className='relative min-h-[100px] w-[30%] max-w-[200px] md:min-h-[120px]'>
          <img
            src={quest.bentoSideImageURL}
            alt={quest.title}
            className='h-full w-full object-cover'
          />
        </div>

        {/* Right side - Content */}
        <div className='relative flex-1 p-3'>
          <VStack className='h-full gap-3'>
            {/* Title */}
            {quest.bentoTitle && <Text font='bold-md'>{quest.bentoTitle}</Text>}

            {/* Badges */}
            <HStack className='flex-wrap items-center gap-2'>
              <div className='flex items-center gap-1.5 rounded-full bg-yellow-50 px-2.5 py-1'>
                <Text font='bold-sm' color='yellow-800'>
                  {quest.mintAmount}
                </Text>
                <img src={parc_logo} width={16} height={16} />
              </div>

              {data?.isPro === true && quest.dailyProLimit !== undefined && (
                <HStack className='items-center gap-2'>
                  <Text font='xs' color='gray-600'>
                    {quest.dailyProLimit} {quest.action}s a day
                  </Text>
                  <Text
                    className='rounded-full bg-secondary-yellow px-2'
                    font='sm'
                  >
                    PRO
                  </Text>
                </HStack>
              )}
              {(data?.isPro === false || data === null) &&
                quest.dailyLimit !== undefined && (
                  <Text font='xs' color='gray-600'>
                    Limit {quest.dailyLimit} {quest.action}
                    {quest.dailyLimit > 1 ? 's' : ''} a day
                  </Text>
                )}
            </HStack>

            {/* Progress section */}
            {data && progressPercentage !== undefined && (
              <HStack className='w-full items-center gap-2'>
                <div className='h-[6px] w-full overflow-hidden rounded-full bg-gray-200'>
                  <div
                    className='h-[6px] rounded-full bg-gradient-to-r from-yellow-400 to-orange-400 shadow-sm transition-all duration-500 ease-out'
                    style={{
                      width: `${progressPercentage}%`,
                    }}
                  ></div>
                </div>
                <Text
                  font='medium-xs'
                  className='whitespace-nowrap text-yellow-800'
                >
                  {data?.dailyEarnings}/{data?.dailyLimit} PARC
                </Text>
              </HStack>
            )}

            {/* Description */}
            <Text font='sm' color='gray-600'>
              {quest.description}
            </Text>

            <VStack className='flex-grow' />

            <HStack className='w-full items-center'>
              {questComplete && (
                <Link
                  to={QuestRoute.to}
                  params={{ questId: quest._id }}
                  className={linkStyles({
                    variant: 'gray',
                    className: 'text-xs',
                  })}
                  viewTransition={
                    isPuzzleWalletWebview ? slideLeftFullPage : undefined
                  }
                >
                  View Quest
                </Link>
              )}
              <HStack className='flex-grow' />
              {/* Start button */}
              {questComplete && data?.isPro === false ? (
                <a
                  href='https://app.puzzle.online/paywall'
                  className={buttonVariants({
                    className: `w-fit min-w-[120px]`,
                  })}
                >
                  <VStack className='-space-x-1 text-center'>
                    <Text font='bold-xs' color='white'>
                      Get Puzzle Pro!
                    </Text>
                    <Text font='xs' color='white'>
                      {quest.dailyLimit !== undefined &&
                      quest.dailyProLimit !== undefined &&
                      quest.action !== undefined
                        ? `Unlock ${
                            quest.dailyProLimit - quest.dailyLimit
                          } more ${quest.action}s`
                        : 'Earn more with Pro'}
                    </Text>
                  </VStack>
                </a>
              ) : (
                <Button
                  onClick={() =>
                    navigate({
                      to: QuestRoute.to,
                      params: { questId: quest._id },
                      viewTransition: isPuzzleWalletWebview
                        ? slideLeftFullPage
                        : undefined,
                    })
                  }
                  className='w-fit min-w-[120px]'
                  disabled={questComplete}
                  size='md'
                >
                  Start
                </Button>
              )}
            </HStack>
          </VStack>
        </div>
      </HStack>
    </div>
  );
};
